import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  PlacementWithLogical,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { FaTwitter } from "react-icons/fa";
import { HiOutlineRefresh } from "react-icons/hi";
import useAuthContext from "~/context/auth.context";
import { app } from "~/core/initFirebase";
import { ProjectDocument } from "~/core/types";
import NotificationModal from "../NotificationModal";
import MinFollowerOnTwitterRequirement from "./MinFollowerOnTwitterRequirement";
import RetweetRequirement from "./RetweetRequirements";
import LikeRequirement from "./LikeRequirements";
import TwitterAccountRequirement from "./TwitterAccountRequirement";
import TwitterAccountToFollowList from "./TwitterAccountToFollowList";

// const db = getFirestore(app);
const functions = getFunctions(app);

export default function ConnectTwitterRequirement({
  project,
  isTooltipOpen,
  tooltipLabel,
  tooltipPosition,
  isTwitterAccountCreatedTooltipOpen,
  isRetweetTooltipOpen,
  isLikeTooltipOpen,
  isMinFollowersTooltipOpen,
  isFollowOnTwitterTooltipOpen,
}: {
  project: ProjectDocument;
  isTooltipOpen: boolean;
  tooltipLabel: string;
  tooltipPosition?: PlacementWithLogical;
  isTwitterAccountCreatedTooltipOpen: boolean;
  isFollowOnTwitterTooltipOpen: string;
  isMinFollowersTooltipOpen: boolean;
  isRetweetTooltipOpen: boolean;
  isLikeTooltipOpen: boolean;
}) {
  const [twitterLoading, setTwitterLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(isTooltipOpen);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [twitterFwdUrl, setTwitterFwdUrl] = useState("");
  const { user, twitterData } = useAuthContext();

  const connectTwitter = () => {
    setTwitterLoading(true);
    const getTwitterUrl = httpsCallable(functions, "tGetUrl");

    getTwitterUrl({ projectId: project?.id }).then((result) => {
      setTwitterLoading(false);
      const data: any = result.data;
      if (isMobile) {
        setTwitterFwdUrl(data);
        setTooltipOpen(false);
        onOpen();
      } else {
        // redirect to Twitter
        window.location.href = data;
      }
    });
  };

  return project?.twitter?.enabled ? (
    <Tooltip
      label={tooltipLabel}
      isOpen={tooltipOpen}
      padding={4}
      fontSize={"md"}
      backgroundColor={"red.500"}
      placement={tooltipPosition}
      hasArrow
    >
      <Box
        px={8}
        py={4}
        w={"100%"}
        color={"descText"}
        borderBottom={"1px"}
        borderColor={"boxBorder"}
        style={{ marginTop: "0", marginBottom: "0" }}
      >
        <Flex direction="column">
          <Box
            w="100%"
            display="flex"
            flexDirection="row"
            alignItems="center"
            mb={4}
          >
            <Icon as={FaTwitter} fontSize="18" mr={2} />
            <Text fontSize="18" fontWeight="500">
              Twitter
            </Text>

            {twitterData && (
              <Flex ml={"auto"} alignItems={"center"}>
                <Text mr={1} mt={"0.5"} fontWeight={500}>
                  @{twitterData.data.username}
                </Text>
                <IconButton
                  colorScheme={"gray"}
                  size={"xs"}
                  variant={"ghost"}
                  icon={<Icon as={HiOutlineRefresh}></Icon>}
                  p={0}
                  fontSize={"18px"}
                  title={"Connect a different account."}
                  onClick={connectTwitter}
                  isLoading={twitterLoading}
                  aria-label={"connect twitter"}
                ></IconButton>
              </Flex>
            )}
            {!twitterData && (
              <Button
                ml={"auto"}
                fontSize={"14"}
                colorScheme={"discord"}
                rounded="full"
                color={"gray.200"}
                fontWeight={400}
                onClick={connectTwitter}
                disabled={!user}
                isLoading={twitterLoading}
              >
                Connect
              </Button>
            )}

            <NotificationModal
              isOpen={isOpen}
              onClose={onClose}
              message={
                "Next, you will be redirected to Twitter to complete the connection request. Please ignore the 'Open in the app' prompts and stay in the browser. Twitter app does not support establishing/authorizing connections in the app."
              }
              successFunction={() => {
                window.location.href = twitterFwdUrl;
              }}
            />
          </Box>
          <TwitterAccountToFollowList
            project={project}
            tooltipPosition={tooltipPosition}
            isTooltipOpen={isFollowOnTwitterTooltipOpen}
          />

          <RetweetRequirement
            isTooltipOpen={isRetweetTooltipOpen}
            tooltipPosition={tooltipPosition}
            project={project}
          />
          <LikeRequirement
            isTooltipOpen={isLikeTooltipOpen}
            tooltipPosition={tooltipPosition}
            project={project}
          />
          <MinFollowerOnTwitterRequirement
            project={project}
            tooltipPosition={tooltipPosition}
            isTooltipOpen={isMinFollowersTooltipOpen}
          />
          <TwitterAccountRequirement
            project={project}
            isTooltipOpen={isTwitterAccountCreatedTooltipOpen}
            tooltipPosition={tooltipPosition}
          />
        </Flex>
      </Box>
    </Tooltip>
  ) : null;
}
